/**
 * The Verify gadgets' plain logic (components/home/gadgets: Tests, Errors, API health, Security). Pure: the Quality
 * overview and runs, the Traces map and requests, the API tab's endpoints go in. A figure the answer does not carry is
 * null, never 0.
 */
import { serviceState } from '../../services/SandboxService';
import { shortServiceName } from '../gitRepoSource';

const ran = (check) => Boolean(check && check.ranAt && check.status !== 'skipped');

/**
 * The tests check of the last run, per service: `{ ran, ranAt, totals, services: [{ service, status, total, failed,
 * coverage, message }] }`; `coverage` the line coverage in percent, null when the service has none.
 */
export function testSummary(overview) {
  const check = overview?.checks?.tests || null;
  if (!ran(check)) { return { ran: false, ranAt: null, totals: null, services: [] }; }
  const details = check.details || {};
  const services = (details.services || check.services || []).map((s) => ({
    service: shortServiceName(s.service),
    status: s.status || null,
    total: Number(s.total) || 0,
    failed: Number(s.failed) || 0,
    coverage: s.coverage && Number.isFinite(Number(s.coverage.lines)) ? Math.round(Number(s.coverage.lines)) : null,
    message: s.message || null,
  }));
  return { ran: true, ranAt: check.ranAt, totals: details.totals || check.counts || null, services };
}

/**
 * The last `max` runs that ran tests (`GET /quality/runs`, newest first), oldest first for a trend:
 * `[{ runId, total, failed, ok, at }]`.
 */
export function testTrend(runs, max = 10) {
  return (runs?.runs || [])
    .filter((run) => run.testTotals && Number(run.testTotals.total) > 0)
    .slice(0, max)
    .map((run) => ({ runId: run.runId, total: Number(run.testTotals.total) || 0, failed: Number(run.testTotals.failed) || 0, ok: !(Number(run.testTotals.failed) > 0), at: run.finishedAt || run.startedAt || null }))
    .reverse();
}

/** The services that crashed or whose build failed: `[{ serviceName, state }]`. */
export const brokenServices = (items = []) => items
  .map(({ serviceName, service }) => ({ serviceName, state: serviceState(service) }))
  .filter(({ state }) => state === 'crashed' || state === 'failed');

/**
 * The failed requests (`GET /traces?status=failed`, newest first): `[{ traceId, entry, service, httpStatus, at,
 * errorCount }]`, at most `max`.
 */
export const failedRequests = (answer, max = 5) => (answer?.traces || [])
  .slice(0, max)
  .map((trace) => ({
    traceId: trace.traceId,
    entry: trace.entry || trace.traceId,
    service: trace.entryService ? shortServiceName(trace.entryService) : null,
    httpStatus: trace.httpStatus || null,
    at: trace.start || null,
    errorCount: trace.errorCount || 0,
  }));

/**
 * API health: the endpoints the services declare (the API tab, `GET /api`), and from the Traces map the services by p95
 * (slowest first; the map times services, not single endpoints) and the operations that failed most.
 * `{ endpoints, services, slowest: [{ service, p95Ms, calls, errors }], failing: [{ name, errors, calls }] }`;
 * `endpoints` null without the API answer, `slowest` empty without requests.
 */
export function apiHealth({ api = null, map = null } = {}) {
  const services = api ? (api.services || []) : null;
  const endpoints = services ? services.reduce((sum, s) => sum + (s.endpoints || []).length, 0) : null;
  const nodes = (map?.nodes || []).filter((n) => n.kind === 'service' && Number.isFinite(n.p95Ms));
  const slowest = nodes
    .map((n) => ({ service: shortServiceName(n.label || n.id), p95Ms: n.p95Ms, calls: n.calls || 0, errors: n.errors || 0 }))
    .sort((a, b) => b.p95Ms - a.p95Ms)
    .slice(0, 3);
  const ops = new Map();
  (map?.edges || []).forEach((edge) => (edge.operations || []).forEach((op) => {
    if (!op?.name || !(op.errors > 0)) { return; }
    const entry = ops.get(op.name) || { name: op.name, errors: 0, calls: 0 };
    entry.errors += op.errors;
    entry.calls += op.calls || 0;
    ops.set(op.name, entry);
  }));
  return {
    endpoints,
    services: services ? services.length : null,
    slowest,
    failing: [...ops.values()].sort((a, b) => b.errors - a.errors).slice(0, 3),
  };
}

/**
 * Security from the Quality overview: critical and high vulnerabilities, critical and high security findings, live
 * secrets in the code, packages whose licence is not allowed (and unknown). Each null when its check has not run.
 */
export function securitySummary(overview) {
  const checks = overview?.checks || {};
  const count = (check, keys) => (ran(check) ? keys.reduce((sum, k) => sum + (Number(check.counts?.[k]) || 0), 0) : null);
  const vulns = checks.vulnerabilities;
  const security = checks.security;
  const secrets = checks.secrets;
  const deps = checks.dependencies;
  const summary = {
    critical: count(vulns, ['critical']),
    high: count(vulns, ['high']),
    risky: count(security, ['critical', 'high']),
    liveSecrets: ran(secrets) ? (Number.isFinite(Number(secrets.counts?.live)) ? Number(secrets.counts.live) : null) : null,
    licences: count(deps, ['notAllowed']),
    unknownLicences: count(deps, ['unknown']),
  };
  summary.ran = Object.values(summary).some((v) => v !== null);
  return summary;
}
